import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

/**
 * Padded section inside a Sheet/Drawer. Title + optional hint + body,
 * with a hairline bottom divider. Matches the design system's
 * SheetSection pattern from sections 9 & 10 of the customer-web
 * redesign canvas.
 */
export function SheetSection({
  title,
  hint,
  children,
  dense,
  noDivider,
}: {
  title?: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
  dense?: boolean;
  noDivider?: boolean;
}) {
  return (
    <section
      className={`${dense ? "px-4 sm:px-6 py-4" : "px-4 sm:px-6 py-5"} ${
        noDivider ? "" : "border-b border-divider"
      }`}
    >
      {(title ?? hint) && (
        <div className="mb-3">
          {title && (
            <div className="text-[13px] font-semibold text-default-900">
              {title}
            </div>
          )}
          {hint && (
            <div className="text-xs text-default-500 mt-1 leading-snug">
              {hint}
            </div>
          )}
        </div>
      )}
      {children}
    </section>
  );
}

/**
 * Per-slot capacity hint shown on pickup time-window tiles.
 *   - remaining === 0  → "· Full"  (danger)
 *   - remaining <= 3    → "· N left" (warning, scarcity nudge)
 *   - remaining > 3     → "· N left" (muted)
 *   - null/undefined    → nothing (uncapped slot, or capacity unknown)
 * Shared by the schedule-pickup wizard and the create-shipment pricing step
 * so the two surfaces can't drift.
 */
export function SlotCapacityLabel({
  remaining,
}: {
  remaining?: number | null;
}) {
  const { t } = useTranslation("app");
  if (remaining === null || remaining === undefined) return null;
  if (remaining === 0) {
    return (
      <span className="text-[11px] font-semibold text-danger-600">
        {t("pickup.capacity.full")}
      </span>
    );
  }
  return (
    <span
      className={`text-[11px] font-semibold ${
        remaining <= 3 ? "text-warning-600" : "text-default-400"
      }`}
    >
      {t("pickup.capacity.left", { count: remaining })}
    </span>
  );
}

/**
 * Selectable card row inside a Sheet — used for address picks, service
 * tier picks, time slots, payment methods. Indigo-50 bg + inset primary
 * ring when selected; white bg + default-200 inset otherwise.
 */
export function SelectableCard({
  selected,
  onSelect,
  disabled,
  children,
  className = "",
}: {
  selected: boolean;
  onSelect?: () => void;
  disabled?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const base =
    "flex items-start gap-3 px-3 py-3 rounded-medium text-left w-full transition-colors";
  const cursor = disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer";
  const skin = selected
    ? "bg-primary-50 ring-[1.5px] ring-inset ring-primary"
    : "bg-content1 ring-1 ring-inset ring-default-200 hover:ring-default-300";

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={disabled ? undefined : onSelect}
      className={`${base} ${cursor} ${skin} ${className}`}
    >
      {children}
    </button>
  );
}

/**
 * Custom radio dot for use inside SelectableCard — matches the design's
 * filled-primary + inset-white-4px pattern.
 */
export function RadioDot({ selected }: { selected: boolean }) {
  return (
    <span
      className={`h-4 w-4 rounded-full flex-shrink-0 mt-0.5 ${
        selected
          ? "bg-primary ring-[4px] ring-inset ring-primary-foreground"
          : "bg-content1 ring-[1.5px] ring-inset ring-default-300"
      }`}
    />
  );
}

/**
 * Custom checkbox box for use inside SelectableCard — matches the
 * design's filled-primary square with white check when selected.
 */
export function CheckBox({ selected }: { selected: boolean }) {
  return (
    <span
      className={`h-[18px] w-[18px] rounded-[4px] flex-shrink-0 mt-0.5 grid place-items-center text-primary-foreground ${
        selected
          ? "bg-primary"
          : "bg-content1 ring-[1.5px] ring-inset ring-default-300"
      }`}
    >
      {selected && (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </span>
  );
}
